import {
	IconCircleCheck,
	IconCircleX,
	IconDots,
	IconLayoutGrid,
	IconPencil,
	IconPlugConnected,
	IconRefresh,
	IconTrash,
} from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Descriptions, List, Modal, Space, Tag, Typography } from "antd";
import { useEffect, useRef, useState } from "react";
import {
	type EnvironmentConnectivityResult,
	type EnvironmentDeleteImpact,
	useDeleteEnvironment,
	useEnvironment,
	useFetchEnvironmentDeleteImpact,
	useUpdateEnvironment,
	useVerifyEnvironmentConnection,
} from "@/common/artemis-operations/environments";
import { useIsWorkspaceAdmin } from "@/common/artemis-operations/members";
import { useIsOrgAdmin } from "@/common/artemis-operations/organization";
import type { UpdateEnvironmentInput } from "@/common/artemis-sdk/graphql";
import {
	DELETE_RESOURCE_DESC,
	DELETE_RESOURCE_ERROR_FAILED,
	DELETE_RESOURCE_INFO_COMPLETED,
	DELETE_RESOURCE_TITLE,
	ENVIRONMENT_DELETE_BLOCKED_DESC,
	ENVIRONMENT_DELETE_BLOCKED_TITLE,
	ENVIRONMENT_DELETE_IMPACT_DESC,
	UPDATE_RESOURCE_ERROR_FAILED,
	UPDATE_RESOURCE_INFO_COMPLETED,
} from "@/common/messages";
import type {
	AutoApprovalThreshold,
	EnvironmentClassification,
	EnvironmentProvider,
} from "@/common/model";
import { EnvironmentStatus } from "@/common/model";
import { GraphQLRequestError } from "@/common/use-gql-client";
import { useNotify } from "@/common/use-notify";
import {
	BaseContainer,
	BasePageContainer,
	CustomIcon,
	QueryGuard,
} from "@/components/base";
import {
	type DeleteImpactRow,
	DeleteImpactTable,
} from "@/components/delete-impact-table";
import {
	METADATA_LABELS,
	SECRET_METADATA_KEYS,
} from "@/components/environments/environment-base";
import type { EnvironmentInitialValues } from "@/components/environments/environment-create-wizard";
import { EnvironmentCreateWizard } from "@/components/environments/environment-create-wizard";
import { EnvironmentDetails } from "@/components/environments/environment-details";
import { useFrictionConfirm } from "@/components/friction-confirm-dialog";
import { useActions } from "@/contexts/actions-context";
import { usePageTitle } from "@/contexts/page-title-context";
import { useWizards } from "@/contexts/wizard-context";

export const Route = createFileRoute(
	"/_authenticated/$orgSlug/workspaces/$workspaceId/environments/$environmentId",
)({ component: EnvironmentPage });

function EnvironmentPage() {
	const { orgSlug, environmentId, workspaceId } = Route.useParams();
	const navigate = useNavigate();
	const { setTitle } = usePageTitle();
	const { setActions } = useActions();
	const { openTemplateGalleryForEnvironment } = useWizards();
	const notify = useNotify();
	const {
		data: environment,
		isPending,
		isError,
		refetch,
	} = useEnvironment(environmentId);
	const updateEnvironment = useUpdateEnvironment(environmentId);
	const deleteEnvironment = useDeleteEnvironment(environmentId);
	const fetchDeleteImpact = useFetchEnvironmentDeleteImpact(environmentId);
	const { verify } = useVerifyEnvironmentConnection(environmentId);
	// Environment management is WorkspaceAdmin-only (or org admin) on the backend — mirror that
	// here so a Participant never sees an edit control that would fail authorization.
	const { isAdmin: isOrgAdmin } = useIsOrgAdmin();
	const canEdit = useIsWorkspaceAdmin(workspaceId) || isOrgAdmin;

	const [editOpen, setEditOpen] = useState(false);
	const [verifyResult, setVerifyResult] =
		useState<EnvironmentConnectivityResult | null>(null);
	const { confirm, dialog } = useFrictionConfirm();
	// Set when a delete is refused because agents/jobs/templates still depend on the environment.
	const [deleteBlockers, setDeleteBlockers] =
		useState<EnvironmentDeleteImpact | null>(null);

	const save = (fields: Omit<UpdateEnvironmentInput, "workspaceId">) =>
		updateEnvironment(
			{ ...fields },
			{
				onSuccess: () => notify.success(UPDATE_RESOURCE_INFO_COMPLETED),
				onError: () => notify.error(UPDATE_RESOURCE_ERROR_FAILED),
			},
		);

	// Refs so the setActions effect never needs to re-run when these change.
	const verifyRef = useRef(() => {});
	verifyRef.current = () => {
		notify.info("Testing connection… checking each configured role.");
		verify(undefined, {
			onSuccess: (result) => {
				setVerifyResult(result);
				const failed = result.checks.filter((c) => !c.ok).length;
				if (result.ok) {
					notify.success("Connection test passed", "All roles reachable.");
				} else {
					notify.error(
						"Connection test failed",
						`${failed} of ${result.checks.length} roles could not be assumed.`,
					);
				}
				// A role we can't assume means agents can't operate here — mark the environment FAILED so
				// it isn't used until the connection is fixed; a passing test flips it back to ACTIVE.
				if (result.checks.length > 0)
					updateEnvironment({
						status: result.ok
							? EnvironmentStatus.ACTIVE
							: EnvironmentStatus.FAILED,
					});
			},
			onError: () =>
				notify.error("Connection test failed", "Could not run the check."),
		});
	};

	const confirmDeleteRef = useRef(() => {});
	confirmDeleteRef.current = async () => {
		// Same check the backend enforces: show what blocks the delete before asking to confirm it.
		let impact: EnvironmentDeleteImpact | null;
		try {
			impact = await fetchDeleteImpact();
		} catch {
			notify.error(DELETE_RESOURCE_ERROR_FAILED);
			return;
		}
		if (impact?.isBlocked) {
			setDeleteBlockers(impact);
			return;
		}
		const rows = impact
			? impactRows(impact, `/${orgSlug}/workspaces/${workspaceId}`)
			: [];
		confirm({
			title: DELETE_RESOURCE_TITLE,
			...(rows.length > 0
				? {
						message: ENVIRONMENT_DELETE_IMPACT_DESC,
						details: <DeleteImpactTable rows={rows} />,
						width: 640,
					}
				: { message: DELETE_RESOURCE_DESC }),
			onConfirm: () => {
				deleteEnvironment(undefined, {
					onSuccess: () => {
						notify.success(DELETE_RESOURCE_INFO_COMPLETED);
						navigate({
							to: "/$orgSlug/workspaces/$workspaceId/environments",
							params: { orgSlug, workspaceId },
						});
					},
					// Show the server's reason, e.g. an agent deployed after the check above.
					onError: (err) =>
						notify.error(
							DELETE_RESOURCE_ERROR_FAILED,
							err instanceof GraphQLRequestError ? err.message : undefined,
						),
				});
			},
		});
	};

	useEffect(() => {
		setTitle(environment?.name ?? "Environment");
	}, [setTitle, environment]);

	useEffect(() => {
		setActions([
			// "Test connection" writes the environment status back, so it is an admin action too.
			...(canEdit
				? ([
						{
							type: "button",
							id: "verify",
							tooltip: "Test connection",
							icon: <CustomIcon icon={IconPlugConnected} />,
							onClick: () => verifyRef.current(),
						},
						{
							type: "button",
							id: "edit",
							icon: <CustomIcon icon={IconPencil} />,
							onClick: () => setEditOpen(true),
						},
					] as const)
				: []),
			{
				type: "button",
				id: "browse-gallery",
				tooltip: "Template gallery",
				icon: <CustomIcon icon={IconLayoutGrid} />,
				onClick: () =>
					environment &&
					openTemplateGalleryForEnvironment({
						id: environment.id,
						provider: environment.provider,
					}),
			},
			{
				type: "submenu",
				id: "settings",
				icon: <CustomIcon icon={IconDots} />,
				submenu: [
					...(canEdit
						? ([
								{
									type: "button",
									id: "delete",
									label: "Delete",
									danger: true,
									icon: <CustomIcon icon={IconTrash} />,
									onClick: () => confirmDeleteRef.current(),
								},
							] as const)
						: []),
					{
						type: "button",
						id: "refresh",
						label: "Refresh",
						icon: <CustomIcon icon={IconRefresh} />,
						onClick: () => refetch(),
					},
				],
			},
		]);
		return () => setActions([]);
	}, [
		setActions,
		refetch,
		canEdit,
		environment,
		openTemplateGalleryForEnvironment,
	]);

	const editInitialValues: EnvironmentInitialValues | undefined = environment
		? {
				name: environment.name,
				provider: environment.provider as EnvironmentProvider,
				classification: environment.classification as EnvironmentClassification,
				autoApproveUpTo: environment.autoApproveUpTo as AutoApprovalThreshold,
				metadata: environment.metadata ?? [],
				allowAgentsToBeDeployed: environment.allowAgentsToBeDeployed,
				allowAgentsToAccess: environment.allowAgentsToAccess,
			}
		: undefined;

	return (
		<QueryGuard isPending={isPending} isError={isError} isNull={!environment}>
			<BasePageContainer>
				<BaseContainer title="Details">
					{/* Omitted onSave* handlers render every field read-only. */}
					<EnvironmentDetails
						environment={environment!}
						onSaveName={canEdit ? (name) => save({ name }) : undefined}
						onSaveClassification={
							canEdit ? (classification) => save({ classification }) : undefined
						}
						onSaveAutoApproveUpTo={
							canEdit
								? (autoApproveUpTo) => save({ autoApproveUpTo })
								: undefined
						}
						onSaveStatus={
							canEdit
								? (status: EnvironmentStatus) => save({ status })
								: undefined
						}
						onSaveAllowAgentsToBeDeployed={
							canEdit
								? (allowAgentsToBeDeployed) => save({ allowAgentsToBeDeployed })
								: undefined
						}
						onSaveAllowAgentsToAccess={
							canEdit
								? (allowAgentsToAccess) => save({ allowAgentsToAccess })
								: undefined
						}
						onSaveOwner={canEdit ? (ownerId) => save({ ownerId }) : undefined}
					/>
				</BaseContainer>

				{environment?.metadata && environment.metadata.length > 0 && (
					<BaseContainer title="Metadata">
						<Descriptions
							style={{ width: "100%" }}
							column={1}
							size="default"
							items={environment.metadata.map(({ key, value }) => ({
								key,
								label: METADATA_LABELS[key] ?? key,
								children: SECRET_METADATA_KEYS.has(key) ? (
									<Tag>Configured</Tag>
								) : (
									value
								),
							}))}
						/>
					</BaseContainer>
				)}
				{dialog}
			</BasePageContainer>

			<Modal
				open={deleteBlockers !== null}
				title={ENVIRONMENT_DELETE_BLOCKED_TITLE}
				onCancel={() => setDeleteBlockers(null)}
				footer={null}
				width={640}
			>
				<Space vertical size="middle" style={{ width: "100%" }}>
					<Typography.Text>{ENVIRONMENT_DELETE_BLOCKED_DESC}</Typography.Text>
					{deleteBlockers && (
						<DeleteImpactTable
							rows={impactRows(
								deleteBlockers,
								`/${orgSlug}/workspaces/${workspaceId}`,
							)}
						/>
					)}
				</Space>
			</Modal>

			<Modal
				open={verifyResult !== null}
				title="Connection test"
				onCancel={() => setVerifyResult(null)}
				onOk={() => setVerifyResult(null)}
				footer={null}
			>
				<List
					dataSource={verifyResult?.checks ?? []}
					renderItem={(check) => (
						<List.Item>
							<Space align="start">
								<CustomIcon
									icon={check.ok ? IconCircleCheck : IconCircleX}
									color={
										check.ok
											? "var(--ant-color-success)"
											: "var(--ant-color-error)"
									}
								/>
								<div>
									<strong>{check.role}</strong>
									<div style={{ color: "var(--ant-color-text-secondary)" }}>
										{check.ok ? "Reachable" : check.message}
									</div>
								</div>
							</Space>
						</List.Item>
					)}
				/>
			</Modal>

			{canEdit && editInitialValues && (
				<EnvironmentCreateWizard
					open={editOpen}
					onClose={() => setEditOpen(false)}
					initialValues={editInitialValues}
					onUpdate={(input) =>
						updateEnvironment(input, {
							onSuccess: () => notify.success(UPDATE_RESOURCE_INFO_COMPLETED),
							onError: () => notify.error(UPDATE_RESOURCE_ERROR_FAILED),
						})
					}
				/>
			)}
		</QueryGuard>
	);
}

/** Flattens an environment's delete impact into table rows; `base` is the workspace's app path. */
function impactRows(
	impact: EnvironmentDeleteImpact,
	base: string,
): DeleteImpactRow[] {
	return [
		...impact.deployments.map((d) => ({
			id: d.id,
			type: "Deployment",
			name: d.name,
			href: `${base}/projects/${d.projectId}?deployment=${d.id}`,
			project: {
				name: d.projectName ?? d.projectId ?? "",
				href: `${base}/projects/${d.projectId}`,
			},
			impact: "deleted" as const,
		})),
		...impact.managedAgents.map((a) => ({
			id: a.id,
			type: "Agent",
			name: a.name,
			href: `${base}/agents/${a.id}`,
			impact: "blocks" as const,
		})),
		...impact.jobs.map((j) => ({
			id: j.id,
			type: "Scheduled job",
			name: j.name,
			href: `${base}/jobs/${j.id}`,
			impact: "blocks" as const,
		})),
		// No template detail page: open the list filtered to this one.
		...impact.taskTemplates.map((t) => ({
			id: t.id,
			type: "Task template",
			name: t.name,
			href: `${base}/task-templates?search=${encodeURIComponent(t.id)}`,
			impact: "blocks" as const,
		})),
		...impact.externalAgents.map((a) => ({
			id: a.id,
			type: "External agent",
			name: a.name,
			href: `${base}/agents/${a.id}`,
			impact: "unlinked" as const,
		})),
	];
}
